import { useEffect, useMemo, useRef, useState } from "react";

export interface SelectOption {
  value: string | number;
  label: string;
}

interface SearchableSelectProps {
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  disabled?: boolean;
  error?: boolean;
  /** When no matching option is found but a value is set, show the raw value as label (used for name-based fields whose option list loads asynchronously). */
  allowRawValue?: boolean;
  className?: string;
  /** Tailwind background class for the trigger box. */
  bgClass?: string;
  /** Tailwind ring color class applied while open. */
  ringClass?: string;
  /** Tailwind sizing/shape classes for the trigger box. */
  sizeClass?: string;
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  error = false,
  allowRawValue = false,
  className = "",
  bgClass = "bg-white",
  ringClass = "ring-pink-400",
  sizeClass = "px-4 py-2 rounded-lg",
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value ?? ""));
  const displayLabel =
    selected?.label ?? (allowRawValue && value !== null && value !== undefined && value !== "" ? String(value) : "");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <div
        onClick={() => { if (!disabled) setOpen((o) => !o); }}
        className={`w-full border flex items-center justify-between ${sizeClass} ${bgClass} ${
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
        } ${error ? "border-red-400" : "border-gray-300"} ${
          displayLabel ? "text-gray-900" : "text-gray-400"
        } ${open ? `ring-2 ${ringClass}` : ""}`}
      >
        <span className="truncate">{displayLabel || placeholder}</span>
        <svg className="w-4 h-4 text-gray-600 shrink-0 ml-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {open && !disabled && (
        <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1">
          <div className="p-2 border-b border-gray-100">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              placeholder={searchPlaceholder}
              className="w-full border border-gray-200 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gray-300"
            />
          </div>
          <div className="max-h-52 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-400">No results found</div>
            ) : (
              filtered.map((o) => (
                <div
                  key={o.value}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  className={`px-4 py-2 cursor-pointer text-sm text-gray-900 hover:bg-gray-100 ${
                    String(o.value) === String(value ?? "") ? "bg-gray-100 font-medium" : ""
                  }`}
                >
                  {o.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
